import apiService from './apiService';

const normalize = (value) => String(value || '').trim().toLowerCase();

const pickProductIdentifier = (product = {}) => (
  product.codigo_barras
  || product.codigo_producto
  || product.codigo_plu_balanza
  || product.nombre
  || ''
);

const pickPresentationIdentifier = (presentation = {}) => (
  presentation.codigo_barras
  || presentation.sku_presentacion
  || presentation.nombre
  || ''
);

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const resolveByIdentifier = async (identifier, quantity) => {
  const { data } = await apiService.get(
    `/api/prices/resolve/${encodeURIComponent(identifier)}`,
    { cantidad_unidades: quantity }
  );
  return data;
};

// Sin `tolerante=1` a propósito: el resultado único se agrega a la venta sin
// que nadie lo elija, y una corrección de tipeo no puede decidir qué se vende.
const searchProductCandidates = async (term, quantity) => {
  const { data } = await apiService.get('/api/prices', {
    search: term,
    hasPrices: true,
    para_venta: 1, // RN-BP1: sólo productos activos
    page: 1,
    limit: 10,
    cantidad_unidades: quantity
  });
  return unwrapList(data)
    .filter((product) => !product?.es_granel)
    .map((product) => ({
      type: 'PRODUCTO',
      identifier: pickProductIdentifier(product),
      exactScore: [
        product.codigo_barras,
        product.codigo_producto,
        product.codigo_plu_balanza,
        product.nombre
      ].some((value) => normalize(value) === normalize(term)) ? 1 : 0
    }));
};

const searchPresentationCandidates = async (term) => {
  const { data } = await apiService.get('/api/product-presentations', {
    search: term,
    includeInactive: false
  });
  return unwrapList(data)
    .filter((presentation) => presentation.es_presentacion_venta !== false)
    .map((presentation) => ({
      type: 'PRESENTACION',
      identifier: pickPresentationIdentifier(presentation),
      exactScore: [
        presentation.codigo_barras,
        presentation.sku_presentacion,
        presentation.nombre
      ].some((value) => normalize(value) === normalize(term)) ? 1 : 0
    }));
};

export const resolveSaleInput = async ({ term, quantity = 1 }) => {
  const cleanTerm = String(term || '').trim();
  const safeQuantity = Number.parseInt(quantity, 10) > 0 ? Number.parseInt(quantity, 10) : 1;
  if (!cleanTerm) {
    const error = new Error('Ingrese código, SKU, barcode o nombre.');
    error.status = 400;
    throw error;
  }

  try {
    return await resolveByIdentifier(cleanTerm, safeQuantity);
  } catch (error) {
    if (error?.response?.status !== 404) throw error;
  }

  const [products, presentations] = await Promise.all([
    searchProductCandidates(cleanTerm, safeQuantity),
    searchPresentationCandidates(cleanTerm)
  ]);
  const candidates = [...products, ...presentations].filter((candidate) => candidate.identifier);
  const exactCandidates = candidates.filter((candidate) => candidate.exactScore > 0);
  const usableCandidates = exactCandidates.length ? exactCandidates : candidates;

  if (usableCandidates.length === 1) {
    return resolveByIdentifier(usableCandidates[0].identifier, safeQuantity);
  }

  const error = new Error(
    usableCandidates.length > 1
      ? 'Se encontraron varios productos o presentaciones. Refiná la búsqueda o usá un código más específico.'
      : 'Producto o presentación no encontrado.'
  );
  error.status = usableCandidates.length > 1 ? 409 : 404;
  throw error;
};

export default {
  resolveSaleInput
};
